import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getMe, getAllTeams, switchTeam, refreshToken as apiRefreshToken, getAccountBillingStatus, getTeamById } from '../lib/api';

import { getTeamRole, isAppAdmin } from '../lib/roles';

import { toast } from 'sonner';
import { availableTeams } from '../lib/accountContexts';

const AuthContext = createContext(null);

// Role definitions matching backend
const ROLES = {
  admin: 100,
  team_admin: 90,
  head_coach: 80,
  assistant_coach: 60,
  coach: 60,
  team_manager: 60,
  player: 40,
  parent: 30
};

const ROLE_NAMES = {
  admin: "Admin",
  team_admin: "Lagadmin",
  head_coach: "Huvudledare",
  assistant_coach: "Assisterande ledare",
  coach: "Tränare",
  team_manager: "Lagledare",
  player: "Spelare",
  parent: "Förälder"
};

// Session-scoped key for restoring Platform Admin support context after a
// same-tab refresh. Stores identifiers only ({orgId, teamId}) — never backend
// state, never localStorage.
const SUPPORT_CTX_KEY = 'trainnova_support_ctx';

// If the user chose "I already have an account" during a social sign-in, the
// pending identity is attached right after they verify their existing account.
// Direct Google linking requires explicit confirmation in SocialContinue.

export function AuthProvider({ children }) {
  const authEpoch = useRef(0);
  const teamDataVersion = useRef(0);
  const teamRequest = useRef(null);
  const [teamSwitching, setTeamSwitching] = useState(false);
  const [teamSwitchError, setTeamSwitchError] = useState(null);
  const isCurrentSession = useCallback((epoch) =>
    epoch === authEpoch.current && Boolean(localStorage.getItem('token')), []);
  const isCurrentTeamRead = useCallback((epoch, version) =>
    isCurrentSession(epoch) && version === teamDataVersion.current &&
    teamRequest.current?.epoch !== epoch, [isCurrentSession]);
  const [user, setUser] = useState(null);
  const [teams, setTeams] = useState(null); // null = not loaded yet, [] = loaded but empty
  const [activeTeam, setActiveTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Platform Admin SUPPORT CONTEXT — ephemeral, route-scoped, NON-persisted to
  // backend. Survives internal navigation + same-tab refresh via sessionStorage
  // (identifiers only), never localStorage. Never written to active_team_id /
  // memberships / user record. Cleared on exit.
  // { orgId, teamId, team } | null
  const [supportContext, setSupportContext] = useState(null);
  const enterSupportMode = useCallback((ctx) => {
    if (!isAppAdmin(user)) return;
    setSupportContext(ctx || null);
    if (ctx?.orgId && ctx?.teamId) {
      try {
        sessionStorage.setItem(SUPPORT_CTX_KEY, JSON.stringify({ orgId: ctx.orgId, teamId: ctx.teamId }));
      } catch (e) { /* ignore storage errors */ }
    }
  }, [user]);
  const exitSupportMode = useCallback(() => {
    setSupportContext(null);
    try { sessionStorage.removeItem(SUPPORT_CTX_KEY); } catch (e) { /* ignore */ }
  }, []);

  // Rehydrate support context after a same-tab refresh. Only app_admin, only if
  // a valid stored context loads via existing authorized API. On any failure the
  // stale entry is cleared and we fall back to normal Platform Admin context.
  useEffect(() => {
    if (!user || user.global_role !== 'app_admin') {
      if (user || !loading) exitSupportMode();
      return;
    }
    if (supportContext) return;
    let stored = null;
    try { stored = JSON.parse(sessionStorage.getItem(SUPPORT_CTX_KEY) || 'null'); } catch (e) { stored = null; }
    if (!stored?.orgId || !stored?.teamId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getTeamById(stored.teamId);
        if (cancelled || !localStorage.getItem('token')) return;
        setSupportContext({ orgId: stored.orgId, teamId: stored.teamId, team: res.data });
      } catch (e) {
        try { sessionStorage.removeItem(SUPPORT_CTX_KEY); } catch (_) { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading, exitSupportMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user needs onboarding (no team or no person_id).
  // A coach with ZERO teams but a valid commercial account (paid/trialing) is
  // allowed straight into the app shell — commercial access is resolved by the
  // backend (`/billing/status` → has_access), the single source of truth.
  const resolveNeedsOnboarding = useCallback(async (userData, teamsData) => {
    if (!userData) return false;

    // Admins and platform admins never need onboarding
    if (isAppAdmin(userData)) {
      return false;
    }

    // User needs onboarding if they have no teams
    const userTeams = userData.team_ids?.length > 0 ? userData.team_ids :
                      (userData.team_id ? [userData.team_id] : []);

    // teamsData can be null (not loaded), [] (loaded but empty), or [...] (has teams)
    const loadedTeams = teamsData || [];
    if (userTeams.length === 0 && loadedTeams.length === 0) {
      // No teams: only force onboarding when the account lacks commercial access.
      try {
        const res = await getAccountBillingStatus();
        if (res?.data?.has_access) return false;
      } catch {
        // Treat billing-status failure as "no access" → onboarding.
      }
      return true;
    }

    return false;
  }, []);

  // ─── Silent JWT refresh (Feb 2026) ────────────────────────────────────
  // Session = 30 days. When remaining life < 7 days, ask the server for
  // a fresh token. Runs on mount + every time the tab becomes visible.
  const maybeRefreshToken = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      // JWT payload is base64(header).base64(payload).base64(sig)
      const parts = token.split('.');
      if (parts.length !== 3) return;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const expSeconds = payload.exp;
      if (!expSeconds) return;
      const msUntilExpiry = expSeconds * 1000 - Date.now();
      const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
      // Only refresh if token is still valid AND has less than 7 days left
      if (msUntilExpiry > 0 && msUntilExpiry < SEVEN_DAYS_MS) {
        const res = await apiRefreshToken();
        if (res?.data?.access_token && localStorage.getItem('token') === token) {
          localStorage.setItem('token', res.data.access_token);
          if (res.data.user) {
            localStorage.setItem('user', JSON.stringify(res.data.user));
          }
        }
      }
    } catch (_err) {
      // Silent: if refresh fails, user will be prompted to login when token actually expires
    }
  }, []);

  useEffect(() => {
    // Run once on app load
    maybeRefreshToken();
    // Run again whenever the tab becomes visible (catches long-idle sessions)
    const onVisible = () => { if (document.visibilityState === 'visible') maybeRefreshToken(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [maybeRefreshToken]);

  // ─── Teams auto-heal (Feb 2026) ───────────────────────────────────────
  // If the user is authenticated but the teams list ended up empty
  // (e.g. a transient network blip during the mount-time Promise.all),
  // silently retry fetching teams up to 3x with backoff. Without this,
  // the sidebar team-switcher only shows "Trainova admin" forever even
  // though /api/teams returns the real list on the next request.
  useEffect(() => {
    if (!user || loading) return;
    // null = still loading initial fetch, [] = loaded but empty → auto-heal
    if (teams === null) return;
    if (Array.isArray(teams) && teams.length > 0) return;

    let cancelled = false;
    let attempt = 0;
    const MAX_ATTEMPTS = 3;

    const tryRefresh = async () => {
      if (cancelled) return;
      attempt += 1;
      const epoch = authEpoch.current;
      const version = teamDataVersion.current;
      try {
        const res = await getAllTeams();
        if (cancelled || !isCurrentTeamRead(epoch, version)) return;
        const list = availableTeams(res.data);
        if (list.length > 0) {
          setTeams(list);
          // Also set active team if it's still null
          const activeId = user.active_team_id || user.team_id;
          const active = list.find(t => t.id === activeId) || list[0] || null;
          setActiveTeam(prev => prev || active);
          return;
        }
      } catch (_err) {
        // silent — retry below if attempts remain
      }
      if (attempt < MAX_ATTEMPTS && !cancelled) {
        // Exponential backoff: 2s, 5s, 10s
        const delay = [2000, 5000, 10000][attempt - 1] || 10000;
        setTimeout(tryRefresh, delay);
      }
    };
    const firstTimer = setTimeout(tryRefresh, 2000);
    return () => {
      cancelled = true;
      clearTimeout(firstTimer);
    };
  }, [user, teams, loading, isCurrentTeamRead]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const epoch = authEpoch.current;
    const version = teamDataVersion.current;
    
    if (token && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      
      // Load teams and verify token
      Promise.all([getMe(), getAllTeams()])
        .then(async ([userRes, teamsRes]) => {
          if (!isCurrentTeamRead(epoch, version)) return;
          setUser(userRes.data);
          localStorage.setItem('user', JSON.stringify(userRes.data));
          const loadedTeams = availableTeams(teamsRes.data);
          setTeams(loadedTeams);
          
          // Check onboarding status
          const needsOnboard = await resolveNeedsOnboarding(userRes.data, loadedTeams);
          if (!isCurrentTeamRead(epoch, version)) return;
          setNeedsOnboarding(needsOnboard);
          
          // Set active team
          const activeId = userRes.data.active_team_id || userRes.data.team_id;
          const active = loadedTeams.find(t => t.id === activeId) || loadedTeams[0] || null;
          setActiveTeam(active);
        })
        .catch((error) => {
          // A 401 interceptor may already have removed the token. Still clear
          // this session's cached UI, but never touch a newer login/selection.
          if (epoch !== authEpoch.current || version !== teamDataVersion.current) return;
          // Only clear auth state if it's actually a 401 authentication error
          // Don't clear for network errors, server errors, or other transient issues
          const status = error?.response?.status;
          const isAuthError = status === 401;
          
          if (isAuthError) {
            console.warn('Auth verification failed with 401, clearing session');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setActiveTeam(null);
            setTeams([]); // Set to empty array so routing can proceed
          } else {
            // For non-auth errors (network issues, 500s, etc.), keep using cached user data
            console.warn('Auth verification failed with non-auth error, keeping cached session:', error?.message || error);
            // User is already set from parsed localStorage data
            // Set teams to empty array so SmartHomeRedirect doesn't wait forever
            setTeams([]);
          }
        })
        .finally(() => { if (epoch === authEpoch.current) setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [resolveNeedsOnboarding, isCurrentSession, isCurrentTeamRead]);

  // Cross-tab / PWA auth sync (Feb 2026)
  // ------------------------------------------------------------------
  // When a magic-link login completes in one window (e.g. Safari after an
  // email click), any other already-open SPA window — including the
  // installed PWA — needs to hydrate without forcing the user to log in
  // again. We listen on two channels:
  //   1. `storage` event   — fires in OTHER tabs/windows of the same
  //                          origin when localStorage is written.
  //   2. BroadcastChannel  — instant, structured message bus for SPAs.
  useEffect(() => {
    const hydrateFromStorage = async () => {
      const epoch = authEpoch.current;
      const version = teamDataVersion.current;
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      if (!token || !savedUser) return;
      try {
        const parsed = JSON.parse(savedUser);
        setActiveTeam(null);
        setTeams(null);
        setTeamSwitchError(null);
        setTeamSwitching(false);
        setUser(parsed);
        // Refresh from server so teams/roles are up-to-date.
        const [userRes, teamsRes] = await Promise.all([getMe(), getAllTeams()]);
        if (!isCurrentTeamRead(epoch, version)) return;
        setUser(userRes.data);
        localStorage.setItem('user', JSON.stringify(userRes.data));
        const loadedTeams = availableTeams(teamsRes.data);
        setTeams(loadedTeams);
        const activeId = userRes.data.active_team_id || userRes.data.team_id;
        setActiveTeam(loadedTeams.find(t => t.id === activeId) || loadedTeams[0] || null);
        const needs = await resolveNeedsOnboarding(userRes.data, loadedTeams);
        if (isCurrentTeamRead(epoch, version)) setNeedsOnboarding(needs);
      } catch {
        // best-effort sync; failures fall back to next normal request flow
      }
    };

    const onStorage = (e) => {
      if (!e || (e.key !== 'token' && e.key !== 'user')) return;
      if (e.key === 'token' && e.newValue) {
        authEpoch.current += 1;
        teamRequest.current = null;
        teamDataVersion.current += 1;
        setTeamSwitching(false);
        setTeamSwitchError(null);
        hydrateFromStorage();
      }
      if (e.key === 'token' && !e.newValue) {
        // Token removed in another tab → invalidate pending reads too.
        authEpoch.current += 1;
        teamRequest.current = null;
        teamDataVersion.current += 1;
        setTeamSwitching(false);
        setTeamSwitchError(null);
        exitSupportMode();
        setNeedsOnboarding(false);
        setUser(null);
        setTeams([]);
        setActiveTeam(null);
      }
    };
    window.addEventListener('storage', onStorage);

    let bc = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('trainova-auth');
        bc.onmessage = (msg) => {
          if (!msg?.data) return;
          if (msg.data.type === 'login' && !user) {
            hydrateFromStorage();
          }
        };
      }
    } catch {
      bc = null;
    }

    return () => {
      window.removeEventListener('storage', onStorage);
      if (bc) {
        try { bc.close(); } catch { /* noop */ }
      }
    };
  }, [user, resolveNeedsOnboarding, isCurrentSession, isCurrentTeamRead, exitSupportMode]);

  const loginUser = async (token, userData) => {
    const epoch = ++authEpoch.current;
    teamRequest.current = null;
    const version = ++teamDataVersion.current;
    setTeamSwitching(false);
    setTeamSwitchError(null);
    setTeams(null);
    exitSupportMode();
    setActiveTeam(null);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Load teams and full user data - CRITICAL: We must wait for this to complete
    // so that SmartHomeRedirect has the correct data to determine
    // the user's role (isCoach) before navigating
    // Also fetch /me to get membership_roles for ParentContext
    try {
      const [userRes, teamsRes] = await Promise.all([getMe(), getAllTeams()]);
      if (!isCurrentTeamRead(epoch, version)) return;
      const fullUserData = userRes.data;
      const loadedTeams = availableTeams(teamsRes.data);
      
      // Update user with full data including membership_roles
      setUser(fullUserData);
      localStorage.setItem('user', JSON.stringify(fullUserData));
      setTeams(loadedTeams);
      
      // Check onboarding status
      const needsOnboard = await resolveNeedsOnboarding(fullUserData, loadedTeams);
      if (!isCurrentTeamRead(epoch, version)) return;
      setNeedsOnboarding(needsOnboard);
      
      const activeId = fullUserData.active_team_id || fullUserData.team_id;
      const active = loadedTeams.find(t => t.id === activeId) || loadedTeams[0] || null;
      setActiveTeam(active);
    } catch (e) {
      if (!isCurrentTeamRead(epoch, version)) return;
      console.error('Failed to load teams:', e);
      // Even on error, set teams to empty array so we know loading is complete
      setTeams([]);
    }
  };

  // Refresh user data from backend
  const refreshUser = async () => {
    const epoch = authEpoch.current;
    const version = teamDataVersion.current;
    try {
      const [userRes, teamsRes] = await Promise.all([getMe(), getAllTeams()]);
      if (!isCurrentTeamRead(epoch, version)) return;
      setUser(userRes.data);
      localStorage.setItem('user', JSON.stringify(userRes.data));
      setTeams(availableTeams(teamsRes.data));
      
      // Re-check onboarding status
      const needsOnboard = await resolveNeedsOnboarding(userRes.data, teamsRes.data);
      if (!isCurrentTeamRead(epoch, version)) return;
      setNeedsOnboarding(needsOnboard);
      
      // Update active team
      const activeId = userRes.data.active_team_id || userRes.data.team_id;
      const list = availableTeams(teamsRes.data);
      const active = list.find(t => t.id === activeId) || list[0] || null;
      setActiveTeam(active);
      
      setTeamSwitchError(null);
      return userRes.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  // Complete onboarding and refresh
  const completeOnboarding = async () => {
    await refreshUser();
    setNeedsOnboarding(false);
  };

  const logout = () => {
    // Capture before clearing. Native fetch avoids the API interceptor attaching
    // a later login's token to this delayed logout request.
    const token = localStorage.getItem('token');
    authEpoch.current += 1;
    teamRequest.current = null;
    teamDataVersion.current += 1;
    setTeamSwitching(false);
    setTeamSwitchError(null);
    exitSupportMode();
    setNeedsOnboarding(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setTeams([]);
    setActiveTeam(null);
    setLoading(false);
    return fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then((response) => {
      if (!response.ok) throw new Error('logout_failed');
      return true;
    }).catch(() => {
      toast.error('Du är utloggad här, men servern kunde inte bekräfta utloggningen. Logga in och försök igen när anslutningen fungerar.');
      return false;
    });
  };

  const logoutAllDevices = async () => {
    const epoch = authEpoch.current;
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Logga in igen för att avsluta alla sessioner.');
    const response = await fetch('/api/auth/logout-all', {
      method: 'POST', credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Alla sessioner kunde inte avslutas. Försök igen.');
    if (!isCurrentSession(epoch)) return false;
    await logout();
    return true;
  };

  const updateUserData = (newData) => {
    if (!localStorage.getItem('token')) return;
    const updated = { ...user, ...newData };
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
  };

  const changeActiveTeam = async (selection) => {
    const teamId = typeof selection === 'string' ? selection : selection?.id;
    const epoch = authEpoch.current;
    if (!teamId || !user?.id || !isCurrentSession(epoch)) {
      throw new Error('Logga in och välj ett giltigt lag.');
    }
    if (teamRequest.current?.epoch === epoch) {
      throw new Error('Ett lagbyte pågår. Vänta tills det är klart.');
    }
    const target = availableTeams(teams).find(team => team.id === teamId);
    if (!target) throw new Error('Laget är inte tillgängligt. Uppdatera laglistan.');
    const request = { epoch };
    teamRequest.current = request;
    teamDataVersion.current += 1;
    setTeamSwitching(true);
    setTeamSwitchError(null);
    let switched = false;
    try {
      await switchTeam(teamId);
      switched = true;
      if (!isCurrentSession(epoch)) throw new Error('Kontot har ändrats. Välj lag igen.');
      // /switch-team returns a user document without canonical membership roles.
      // Fetch /me before committing either the user or the selected team.
      const [userRes, teamsRes] = await Promise.all([getMe(), getAllTeams()]);
      if (!isCurrentSession(epoch)) throw new Error('Kontot har ändrats. Välj lag igen.');
      const loadedTeams = availableTeams(teamsRes.data);
      const team = loadedTeams.find(item => item.id === teamId);
      if (!team || userRes.data?.id !== user.id || userRes.data.active_team_id !== teamId) {
        throw new Error('Lagvalet kunde inte bekräftas. Läs in kontot igen.');
      }
      exitSupportMode();
      setUser(userRes.data);
      localStorage.setItem('user', JSON.stringify(userRes.data));
      setTeams(loadedTeams);
      setActiveTeam(team);
      return team;
    } catch (error) {
      if (isCurrentSession(epoch)) {
        const status = error?.response?.status;
        // A denied request leaves the old workspace intact. A lost response or
        // failed read after a successful write requires reconciliation first.
        if (switched || ![400, 401, 403, 404, 409, 422].includes(status)) {
          setTeamSwitchError('Lagbytet kunde inte bekräftas. Läs in kontot igen innan du fortsätter.');
        }
      }
      throw error;
    } finally {
      if (teamRequest.current === request) {
        teamRequest.current = null;
        teamDataVersion.current += 1;
        if (isCurrentSession(epoch)) setTeamSwitching(false);
      }
    }
  };

  const refreshTeams = async () => {
    const epoch = authEpoch.current;
    const version = teamDataVersion.current;
    try {
      const teamsRes = await getAllTeams();
      if (!isCurrentTeamRead(epoch, version)) return;
      setTeams(availableTeams(teamsRes.data));
    } catch (e) {
      console.error('Failed to refresh teams:', e);
    }
  };

  // Get user's role in a specific team (or active team)
  const getUserRole = useCallback((teamId = null) => {
    if (!user) return null;

    // Platform Admin support mode: app_admin acts as full team leader for the
    // support team only. Backend already authorizes app_admin globally; this is
    // a frontend visibility override, NOT a membership.
    if (supportContext && user.global_role === 'app_admin' &&
        (!teamId || teamId === supportContext.teamId)) {
      return 'admin';
    }

    if (isAppAdmin(user)) return 'admin';
    const tid = teamId || activeTeam?.id;
    if (!tid) return null;
    const role = getTeamRole(user, tid);
    // A membership alias never makes the person a platform administrator.
    return role === 'admin' ? 'team_admin' : role;
  }, [user, activeTeam, supportContext]);

  // Get role level (higher = more permissions)
  const getRoleLevel = useCallback((role) => {
    return ROLES[role] || 0;
  }, []);

  // Check if user has at least the specified role in current/specified team
  const hasPermission = useCallback((minRole, teamId = null) => {
    const userRole = getUserRole(teamId);
    const required = getRoleLevel(minRole);
    return required > 0 && getRoleLevel(userRole) >= required;
  }, [getUserRole, getRoleLevel]);

  // Role checks for current team
  const isAdmin = isAppAdmin(user);
  const isHeadCoach = hasPermission('head_coach');
  const isAssistantCoach = hasPermission('assistant_coach');
  const isLeader = hasPermission('assistant_coach'); // Leader = head_coach or assistant_coach
  const isPlayer = getUserRole() === 'player';
  const isParent = getUserRole() === 'parent';
  
  // Legacy compatibility
  const isCoach = isLeader;

  // Get user's team IDs (from teams list returned by /me endpoint, ONLY source)
  const userTeamIds = user?.teams?.length > 0 
    ? user.teams.map(t => t.team_id)
    : (user?.active_team_id ? [user.active_team_id] : []);

  // Get current role name for display
  const currentRoleName = ROLE_NAMES[getUserRole()] || getUserRole();

  // Support mode makes the support team the EFFECTIVE current team for all
  // team-aware components (central override). Normal membership activeTeam is
  // untouched and restored the moment support mode is cleared.
  const isSupportMode = isAppAdmin(user) && !!supportContext;
  const effectiveActiveTeam = (isSupportMode && supportContext?.team) || activeTeam;

  return (
    <AuthContext.Provider value={{ 
      user, 
      teams,
      activeTeam: effectiveActiveTeam,
      membershipActiveTeam: activeTeam,
      userTeamIds,
      loading,
      sessionVersion: authEpoch.current,
      teamSwitching,
      teamSwitchError,
      needsOnboarding,
      loginUser, 
      logout,
      logoutAllDevices,
      updateUserData,
      refreshUser,
      completeOnboarding,
      changeActiveTeam,
      refreshTeams,
      // Platform Admin support context (ephemeral)
      isSupportMode,
      supportContext: isSupportMode ? supportContext : null,
      enterSupportMode,
      exitSupportMode,
      // Role helpers
      getUserRole,
      hasPermission,
      currentRoleName,
      ROLE_NAMES,
      // Role checks
      isAdmin,
      isHeadCoach,
      isAssistantCoach,
      isLeader,
      isCoach, // Legacy
      isPlayer,
      isParent,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
